import React, { memo, useMemo } from 'react';
import {
    LineChart,
    BarChart,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Line,
    Bar,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    AreaChart,
    Area,
    ScatterChart,
    Scatter,
    RadialBarChart,
    RadialBar,
    ComposedChart,
} from 'recharts';

import { RechartsConfig } from '@/shared/types/chart';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'next-themes';
import { useIsMobile } from '@/shared/hooks/use-mobile';

interface RechartsRendererProps {
    config: RechartsConfig;
    title?: string;
    className?: string;
}

interface YAxisConfig {
    dataKey: string;
    displayName: string;
    orientation?: 'left' | 'right';
    type?: 'line' | 'bar' | 'area' | 'scatter' | 'radialBar' | 'pie';
}

type RawYAxisItem = string | {
    dataKey?: string;
    name?: string;
    orientation?: string;
    type?: string;
};

type YAxisDomain = [number | 'auto', number | 'auto'];

interface TooltipPayloadEntry {
    color?: string;
    fill?: string;
    name?: string;
    dataKey?: string | number;
    value?: number | string;
    payload?: {
        fill?: string;
        [key: string]: unknown;
    };
}

const CHART_COLORS_LIGHT = [
    '#2563eb',  /* blue-600 — cor primária em séries únicas */
    '#0f766e',  /* teal-700 — brand */
    '#7c3aed',  /* violet-700 */
    '#ea580c',  /* orange-600 */
    '#dc2626',  /* red-600 */
    '#0891b2',  /* cyan-600 */
    '#4f46e5',  /* indigo-600 */
    '#16a34a',  /* green-600 */
    '#be123c',  /* rose-700 */
    '#a16207',  /* amber-700 */
];

const CHART_COLORS_DARK = [
    '#2dd4bf',  /* teal-400 — brand */
    '#60a5fa',  /* blue-400 */
    '#a78bfa',  /* violet-400 */
    '#fb923c',  /* orange-400 */
    '#f87171',  /* red-400 */
    '#22d3ee',  /* cyan-400 */
    '#818cf8',  /* indigo-400 */
    '#4ade80',  /* green-400 */
    '#fb7185',  /* rose-400 */
    '#fbbf24',  /* amber-400 */
];

const CHART_THEME_LIGHT = {
    grid: '#e2e8f0',
    axis: '#64748b',
    axisLine: '#cbd5e1',
    text: '#0f172a',
    mutedText: '#475569',
    dotFill: '#ffffff',
    pieStroke: '#ffffff',
    pieStrokeWidth: 1,
    polarGrid: '#e2e8f0',
};

const CHART_THEME_DARK = {
    grid: '#2d333e',
    axis: '#8b96a8',
    axisLine: '#2d333e',
    text: '#edeef2',
    mutedText: '#8b96a8',
    dotFill: '#131720',
    /* Mesma cor do card — separa fatias sem borda branca gritante */
    pieStroke: '#131720',
    pieStrokeWidth: 2,
    polarGrid: '#2d333e',
};


// Funcao para converter strings como "1.7MM" ou "1.7K" para numeros
const parseFormattedNumber = (value: string): number => {
    if (typeof value !== 'string') return Number(value) || 0;

    if (/^-?\d+(\.\d+)?$/.test(value)) {
        return parseFloat(value);
    }

    const multipliers: { [key: string]: number } = {
        'K': 1000,
        'M': 1000000,
        'MM': 1000000,
        'B': 1000000000,
    };

    let sanitizedValue = value.replace(/R\$\s?/, '').trim();

    if (sanitizedValue.includes(',')) {
        sanitizedValue = sanitizedValue.replace(/\./g, '').replace(',', '.');
    } else if ((sanitizedValue.match(/\./g) || []).length > 1) {
        sanitizedValue = sanitizedValue.replace(/\./g, '');
    }

    const match = sanitizedValue.match(/^([\d.-]+)([KMB]+)?$/i);

    if (match) {
        const number = parseFloat(match[1]);
        const unit = match[2]?.toUpperCase();
        return number * (multipliers[unit] || 1);
    }

    return parseFloat(sanitizedValue) || 0;
};

// Funcao para converter todo o conjunto de dados
const convertDataToNumbers = (
    data: { [key: string]: string | number }[],
    yAxis: string[],
) => {
    return data.map((row) => {
        const newRow = { ...row };
        yAxis.forEach((key) => {
            if (typeof row[key] === 'string') {
                newRow[key] = parseFormattedNumber(row[key] as string);
            }
        });
        return newRow;
    });
};

// Formata numeros no formato brasileiro
const formatNumber = (value: number) => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (absValue >= 1000000000) {
        const formatted = (absValue / 1000000000).toFixed(1).replace('.', ',');
        return `${sign}${formatted} Bi`;
    }
    if (absValue >= 1000000) {
        const formatted = (absValue / 1000000).toFixed(1).replace('.', ',');
        return `${sign}${formatted} Mi`;
    }
    if (absValue >= 1000) {
        const formatted = (absValue / 1000).toFixed(1).replace('.', ',');
        return `${sign}${formatted} K`;
    }
    return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
};

const getTooltipEntryColor = (entry: TooltipPayloadEntry, fallback: string) => {
    // Em PieChart, a cor real costuma vir em payload.fill (Cell).
    // Em séries cartesianas, normalmente vem em entry.color.
    return (
        entry?.payload?.fill ||
        entry?.color ||
        entry?.fill ||
        fallback
    );
};

/**
 * Decodifica recursivamente escapes literais \uXXXX nos valores de um config já
 * parseado (título, rótulos em data, etc.). O Gemini emite acentos como escapes
 * literais; mensagens antigas foram salvas duplo-escapadas ("\\u00c1rea"), e após
 * o JSON.parse o valor vira a string "Área", exibida como caracteres estranhos.
 * Decodificar aqui (no objeto, não na string crua) é seguro e cobre tanto o render
 * inline (MarkdownRenderer) quanto a aba "Gráficos" (ChartSection) — ambos passam
 * por este componente — corrigindo histórico e mensagens novas.
 */
function decodeUnicodeEscapesDeep<T>(value: T): T {
    if (typeof value === 'string') {
        return (value.includes('\\u')
            ? value.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
            : value) as T;
    }
    if (Array.isArray(value)) {
        return value.map((item) => decodeUnicodeEscapesDeep(item)) as T;
    }
    if (value && typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(value)) {
            out[key] = decodeUnicodeEscapesDeep(val);
        }
        return out as T;
    }
    return value;
}

export function RechartsRenderer({ config: rawConfig, title: rawTitle, className = "" }: RechartsRendererProps) {

    const config = useMemo(() => decodeUnicodeEscapesDeep(rawConfig), [rawConfig]);
    const title = useMemo(() => decodeUnicodeEscapesDeep(rawTitle), [rawTitle]);

    const { t } = useTranslation("shared");
    const { resolvedTheme } = useTheme();
    const isDark = resolvedTheme === 'dark';
    const isMobile = useIsMobile();
    const yAxisWidth = isMobile ? 52 : 74;
    const CHART_THEME = isDark ? CHART_THEME_DARK : CHART_THEME_LIGHT;
    const CHART_COLORS = isDark ? CHART_COLORS_DARK : CHART_COLORS_LIGHT;

    const hasRenderableData = Boolean(config && Array.isArray(config.data) && config.data.length > 0);
    const chartType = config?.chartType || 'BarChart';
    const data = useMemo(() => (Array.isArray(config?.data) ? config.data : []), [config?.data]);
    const xAxis = config?.xAxis || '';
    const yAxis = useMemo(() => (config?.yAxis ? config.yAxis : []), [config?.yAxis]);
    const xAxisKey = typeof xAxis === 'string' ? xAxis : xAxis?.dataKey || '';

    const yAxisConfigs = useMemo((): YAxisConfig[] => {
        if (!yAxis) return [];

        const rawArray = (Array.isArray(yAxis) ? yAxis : [yAxis]) as RawYAxisItem[];

        return rawArray.map((item): YAxisConfig => {
            // Fallback de segurança (usamos dicionário para receber dataKey e displayName diferentes)
            if (typeof item === 'string') {
                return {
                    dataKey: item,
                    displayName: item,
                    orientation: 'left' as const,
                    type: 'line' as const
                };
            }

            const key = item.dataKey || item.name || '';
            return {
                dataKey: String(key),
                displayName: String(item.name || item.dataKey || ''),
                orientation: (item.orientation === 'right' ? 'right' : 'left') as 'left' | 'right',
                type: (
                    item.type === 'bar' ||
                    item.type === 'area' ||
                    item.type === 'scatter' ||
                    item.type === 'radialBar'
                        ? item.type
                        : 'line'
                ) as 'line' | 'bar' | 'area' | 'scatter' | 'radialBar'
            };
        }).filter(c => c.dataKey !== '');
    }, [yAxis]);

    const yAxisArray = useMemo(() => yAxisConfigs.map(c => c.dataKey), [yAxisConfigs]);

    const hasValidYAxis = yAxisArray.length > 0;

    const convertedData = useMemo(() => {
        // Para BarChart horizontal, não converter as categorias (yAxis) para número
        const isHorizontalBar = chartType === 'BarChart' &&
            yAxisArray.length > 0 &&
            data.length > 0 &&
            data.every(item => {
                const yValue = item[yAxisArray[0]];
                return typeof yValue === 'string';
            });

        if (isHorizontalBar) {
            // Converter apenas os valores (xAxis), não as categorias (yAxis)
            const result = data.map((row) => {
                const newRow = { ...row };
                if (typeof row[xAxisKey] === 'string') {
                    newRow[xAxisKey] = parseFormattedNumber(row[xAxisKey] as string);
                }
                return newRow;
            });
            return result;
        }

        // Comportamento padrão: converter yAxis
        const result = convertDataToNumbers(data, yAxisArray);
        return result;
    }, [data, yAxisArray, chartType, xAxisKey]);

    const getDomainForSeries = React.useCallback((seriesKeys: string[]): YAxisDomain => {
        const numericValues = convertedData
            .flatMap((row) => seriesKeys.map((key) => Number(row[key])))
            .filter((value) => Number.isFinite(value));

        if (numericValues.length === 0) {
            return ['auto', 'auto'];
        }

        const minValue = Math.min(...numericValues);
        const maxValue = Math.max(...numericValues);
        if (minValue === maxValue) {
            const padding = Math.max(Math.abs(minValue) * 0.05, 1);
            return [minValue - padding, maxValue + padding];
        }

        const span = maxValue - minValue;
        const padding = span * 0.12;
        return [minValue - padding, maxValue + padding];
    }, [convertedData]);

    const leftSeriesKeys = yAxisConfigs
        .filter((cfg) => (cfg.orientation || 'left') === 'left')
        .map((cfg) => cfg.dataKey);
    const rightSeriesKeys = yAxisConfigs
        .filter((cfg) => cfg.orientation === 'right')
        .map((cfg) => cfg.dataKey);

    const leftAxisDomain = useMemo(() => getDomainForSeries(leftSeriesKeys), [getDomainForSeries, leftSeriesKeys]);
    const rightAxisDomain = useMemo(() => getDomainForSeries(rightSeriesKeys), [getDomainForSeries, rightSeriesKeys]);

    const renderLines = () =>
        yAxisConfigs.map((cfg, index) => (
            <Line
                key={cfg.dataKey}
                type="monotone"
                dataKey={cfg.dataKey}
                name={cfg.displayName}
                stroke={CHART_COLORS[index % CHART_COLORS.length]}
                strokeWidth={3}
                yAxisId={cfg.orientation || 'left'}
                dot={{ r: 2, strokeWidth: 2, fill: CHART_THEME.dotFill }}
                activeDot={{ r: 5, strokeWidth: 2 }}
            />
        ));

    const renderBars = () =>
        yAxisConfigs.map((cfg, index) => (
            <Bar
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                name={cfg.displayName}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                yAxisId={cfg.orientation || 'left'}
                radius={[6, 6, 0, 0]}
                maxBarSize={52}
            />
        ));

    const renderStackedBars = () =>
        yAxisConfigs.map((cfg, index) => (
            <Bar
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                stackId="a"
                name={cfg.displayName}
                yAxisId={cfg.orientation || 'left'}
                radius={[4, 4, 0, 0]}
                maxBarSize={52}
            />
        ));

    const renderAreas = () =>
        yAxisConfigs.map((cfg, index) => (
            <Area
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                stroke={CHART_COLORS[index % CHART_COLORS.length]}
                name={cfg.displayName}
                yAxisId={cfg.orientation || 'left'}
                fillOpacity={0.18}
                strokeWidth={2.5}
            />
        ));

    const renderPies = () => {
        const firstCfg = yAxisConfigs[0];
        if (!firstCfg) return null;
        return (
            <Pie
                data={convertedData}
                dataKey={firstCfg.dataKey}
                nameKey={xAxisKey}
                cx="50%"
                cy="50%"
                outerRadius={120}
                innerRadius={50}
                paddingAngle={isDark ? 3 : 2}
                cornerRadius={4}
                stroke={CHART_THEME.pieStroke}
                strokeWidth={CHART_THEME.pieStrokeWidth}
            >
                {convertedData.map((_entry, index) => {
                    const fill = CHART_COLORS[index % CHART_COLORS.length];
                    return (
                        <Cell
                            key={`cell-${index}`}
                            fill={fill}
                            stroke={CHART_THEME.pieStroke}
                            strokeWidth={CHART_THEME.pieStrokeWidth}
                        />
                    );
                })}
            </Pie>
        );
    };

    const renderRadars = () =>
        yAxisConfigs.map((cfg, index) => (
            <Radar
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                stroke={CHART_COLORS[index % CHART_COLORS.length]}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                fillOpacity={0.28}
                name={cfg.displayName}
                strokeWidth={2}
            />
        ));

    const renderRadialBars = () =>
        yAxisConfigs.map((cfg, index) => (
            <RadialBar
                key={cfg.dataKey}
                dataKey={cfg.dataKey}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                cornerRadius={5}
                name={cfg.displayName}
                stroke={CHART_THEME.pieStroke}
                strokeWidth={CHART_THEME.pieStrokeWidth}
            />
        ));

    const renderScatter = () =>
        yAxisConfigs.map((cfg, index) => (
            <Scatter
                key={cfg.dataKey}
                data={data}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                dataKey={cfg.dataKey}
                name={cfg.displayName}
                opacity={0.85}
            />
        ));

    const legendStyle = {
        color: CHART_THEME.mutedText,
        fontSize: 12,
        fontWeight: 500,
        paddingTop: 8,
    } as const;

    const axisProps = {
        tick: { fill: CHART_THEME.axis, fontSize: 12 },
        axisLine: { stroke: CHART_THEME.axisLine },
        tickLine: { stroke: CHART_THEME.axisLine },
    };

    const renderTooltip = ({ active, payload, label }: { active?: boolean; payload?: TooltipPayloadEntry[]; label?: string | number }) => {
        if (!active || !payload || payload.length === 0) return null;

        return (
            <div
                className={
                    isDark
                        ? 'rounded-lg px-3 py-2 shadow-xl border border-border/50 bg-card/95 backdrop-blur-sm min-w-44'
                        : 'rounded-lg px-3 py-2 shadow-xl border border-slate-200/80 bg-white/95 backdrop-blur-sm min-w-44'
                }
            >
                {label !== undefined && (
                    <div
                        className={
                            isDark
                                ? 'text-[11px] mb-1.5 text-muted-foreground font-medium'
                                : 'text-[11px] mb-1.5 text-slate-600 font-medium'
                        }
                    >
                        {label}
                    </div>
                )}
                <div className="space-y-1">
                    {payload.map((entry, idx) => {
                        const fallbackColor = CHART_COLORS[idx % CHART_COLORS.length];
                        return (
                        <div key={`${entry?.dataKey}-${idx}`} className="flex items-center justify-between gap-3 text-[11px]">
                            <span
                                className={
                                    isDark
                                        ? 'flex items-center gap-1.5 text-foreground/90'
                                        : 'flex items-center gap-1.5 text-slate-700'
                                }
                            >
                                <span
                                    className="inline-block w-2 h-2 rounded-full"
                                    style={{ backgroundColor: getTooltipEntryColor(entry, fallbackColor) }}
                                />
                                {entry?.name || entry?.dataKey}
                            </span>
                            <span
                                className={
                                    isDark ? 'font-semibold text-foreground' : 'font-semibold text-slate-900'
                                }
                            >
                                {formatNumber(Number(entry?.value || 0))}
                            </span>
                        </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderComposedChart = () => {
        const hasRightAxis = yAxisConfigs.some(c => c.orientation === 'right');

        return (
            <ComposedChart data={convertedData} margin={chartMargin}>
                <CartesianGrid stroke={CHART_THEME.grid} strokeDasharray="3 4" />
                <XAxis dataKey={xAxisKey} angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight} {...axisProps} />

                {/* Eixo Y Principal (Esquerdo) */}
                <YAxis
                    yAxisId="left"
                    orientation="left"
                    tickFormatter={formatNumber}
                    width={yAxisWidth}
                    {...axisProps}
                />

                {/* Eixo Y Secundário (Direito) - Só renderiza se houver dados para ele */}
                {hasRightAxis && (
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickFormatter={formatNumber}
                        width={yAxisWidth}
                        {...axisProps}
                    />
                )}

                <Tooltip content={renderTooltip} />
                <Legend layout="horizontal" verticalAlign="top" wrapperStyle={legendStyle} />

                {yAxisConfigs.map((cfg, idx) => {
                    const chartProps = {
                        dataKey: cfg.dataKey,
                        yAxisId: cfg.orientation || 'left',
                        name: cfg.displayName,
                        stroke: CHART_COLORS[idx % CHART_COLORS.length],
                        fill: CHART_COLORS[idx % CHART_COLORS.length],
                    };

                    if (cfg.type === 'bar') {
                        return <Bar key={cfg.dataKey} {...chartProps} barSize={24} radius={[6, 6, 0, 0]} />;
                    }
                    if (cfg.type === 'area') {
                        return <Area key={cfg.dataKey} {...chartProps} fillOpacity={0.3} />;
                    }
                    return (
                        <Line
                            key={cfg.dataKey}
                            {...chartProps}
                            type="monotone"
                            strokeWidth={2.5}
                            dot={{ r: 2, strokeWidth: 2, fill: CHART_THEME.dotFill }}
                            activeDot={{ r: 5, strokeWidth: 2 }}
                        />
                    );
                })}
            </ComposedChart>
        );
    };

    const hasRightAxis = yAxisConfigs.some(c => c.orientation === 'right');

    const xAxisValues = useMemo(
        () => convertedData.map((row) => String(row[xAxisKey] ?? '')),
        [convertedData, xAxisKey]
    );

    const maxXAxisLabelLength = useMemo(
        () => xAxisValues.reduce((max, value) => Math.max(max, value.length), 0),
        [xAxisValues]
    );

    /**
     * Espaço do eixo X: com rotação, o footprint vertical das labels vai quase todo
     * para `XAxis.height` (área reservada pelo Recharts). `margin.bottom` fica mínimo
     * para não duplicar a mesma reserva (evita faixa branca enorme embaixo).
     */
    const xAxisLayout = useMemo(() => {
        const count = xAxisValues.length;
        const maxLen = maxXAxisLabelLength;
        const rotate = count > 6 || maxLen > 10;

        if (!rotate) {
            return {
                rotate: false,
                angle: 0 as const,
                textAnchor: 'middle' as const,
                height: 32,
                marginBottom: 10,
            };
        }

        const charEstPx = 6.5;
        const angleDeg = 38;
        const angleRad = (angleDeg * Math.PI) / 180;
        // Altura única necessária para o texto inclinado (sem somar de novo em margin.bottom)
        const projected = maxLen * charEstPx * Math.sin(angleRad);
        const height = Math.min(300, Math.max(52, Math.round(projected + 28)));

        return {
            rotate: true,
            angle: -38 as const,
            textAnchor: 'end' as const,
            height,
            marginBottom: 12,
        };
    }, [xAxisValues, maxXAxisLabelLength]);

    const shouldRotateXAxisLabels = xAxisLayout.rotate;
    const xAxisAngle = xAxisLayout.angle;
    const xAxisTextAnchor = xAxisLayout.textAnchor;
    const xAxisHeight = xAxisLayout.height;

    const chartMargin = useMemo(
        () => ({
            top: 14,
            right: hasRightAxis ? 20 : 12,
            left: 8,
            bottom: xAxisLayout.marginBottom,
        }),
        [hasRightAxis, xAxisLayout.marginBottom]
    );

    const isHorizontalBarChart = useMemo(() => {
        if (chartType !== 'BarChart') return false;
        if (yAxisArray.length === 0 || convertedData.length === 0) return false;
        return convertedData.every(item => {
            const xValue = item[xAxisKey];
            const yValue = item[yAxisArray[0]];
            return typeof xValue === 'number' && typeof yValue === 'string';
        });
    }, [chartType, convertedData, yAxisArray, xAxisKey]);

    const chartHeight = useMemo(() => {
        if (chartType === 'PieChart') return 360;
        if (chartType === 'RadarChart' || chartType === 'RadialBarChart') return 400;
        if (isHorizontalBarChart) {
            const dynamic = 150 + convertedData.length * 28;
            return Math.max(320, Math.min(560, dynamic));
        }

        const usesRotatingCategoryX =
            chartType === 'LineChart' ||
            chartType === 'AreaChart' ||
            chartType === 'ComposedChart' ||
            chartType === 'BarChart' ||
            chartType === 'StackedBarChart' ||
            chartType === 'ScatterChart';

        if (usesRotatingCategoryX) {
            // plot + legenda interna; bottomReserve ≈ só xAxis.height + margin mínimo
            const plotAndLegend = 226;
            const bottomReserve = xAxisLayout.height + xAxisLayout.marginBottom;
            return Math.min(760, Math.max(288, plotAndLegend + bottomReserve + 14));
        }

        return 320;
    }, [
        chartType,
        isHorizontalBarChart,
        convertedData.length,
        xAxisLayout.height,
        xAxisLayout.marginBottom,
    ]);

    const renderChart = () => {
        if (!hasValidYAxis) {
            return <p>{t("charts.invalidYAxis")}</p>;
        }

        switch (chartType) {
            case 'LineChart':
                return (
                    <LineChart data={convertedData} margin={chartMargin}>
                        <CartesianGrid stroke={CHART_THEME.grid} strokeDasharray="3 4" />
                        <XAxis dataKey={xAxisKey} angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight} {...axisProps} />
                        <YAxis yAxisId="left" tickFormatter={formatNumber} width={yAxisWidth} domain={leftAxisDomain} {...axisProps} />
                        {hasRightAxis && <YAxis yAxisId="right" orientation="right" tickFormatter={formatNumber} width={yAxisWidth} domain={rightAxisDomain} {...axisProps} />}
                        <Tooltip content={renderTooltip} />
                        <Legend layout="horizontal" verticalAlign="top" align="center" wrapperStyle={legendStyle} />
                        {renderLines()}
                    </LineChart>
                );
            case 'BarChart':
            case 'StackedBarChart':
                // Detectar se é um gráfico horizontal: yAxis contém categorias (strings) e xAxis contém números
                // Quando a config tem yAxis=["conta"] e xAxis="analise_vertical", é horizontal
                if (isHorizontalBarChart) {
                    // BarChart Horizontal: valores no eixo X (xAxisKey), categorias no eixo Y (yAxisArray[0])
                    return (
                        <BarChart
                            data={convertedData}
                            margin={{ top: 14, right: 20, left: 20, bottom: 8 }}
                            layout="vertical"
                        >
                            <CartesianGrid stroke={CHART_THEME.grid} strokeDasharray="3 4" />
                            <XAxis type="number" tickFormatter={formatNumber} {...axisProps} />
                            <YAxis dataKey={yAxisArray[0]} type="category" width={120} {...axisProps} />
                            <Tooltip content={renderTooltip} />
                            <Legend wrapperStyle={legendStyle} />
                            <Bar
                                dataKey={xAxisKey}
                                fill={CHART_COLORS[0]}
                                name={xAxisKey}
                                radius={[0, 6, 6, 0]}
                                maxBarSize={32}
                            />
                        </BarChart>
                    );
                }

                // BarChart Vertical (padrão)
                return (
                    <BarChart data={convertedData} margin={chartMargin}>
                        <CartesianGrid stroke={CHART_THEME.grid} strokeDasharray="3 4" />
                        <XAxis dataKey={xAxisKey} angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight} {...axisProps} />
                        <YAxis yAxisId="left" tickFormatter={formatNumber} width={yAxisWidth} {...axisProps} />
                        {hasRightAxis && <YAxis yAxisId="right" orientation="right" tickFormatter={formatNumber} width={yAxisWidth} {...axisProps} />}
                        <Tooltip content={renderTooltip} />
                        <Legend layout="horizontal" verticalAlign="top" align="center" wrapperStyle={legendStyle} />
                        {chartType === 'StackedBarChart' ? renderStackedBars() : renderBars()}
                    </BarChart>
                );
            case 'PieChart':
                return (
                    <PieChart>
                        {renderPies()}
                        <Tooltip content={renderTooltip} />
                        <Legend
                            wrapperStyle={legendStyle}
                            iconType="circle"
                            iconSize={8}
                            formatter={(value) => (
                                <span style={{ color: CHART_THEME.mutedText }}>{value}</span>
                            )}
                        />
                    </PieChart>
                );
            case 'AreaChart':
                return (
                    <AreaChart data={convertedData} margin={chartMargin}>
                        <CartesianGrid stroke={CHART_THEME.grid} strokeDasharray="3 4" />
                        <XAxis dataKey={xAxisKey} angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight} {...axisProps} />
                        <YAxis yAxisId="left" tickFormatter={formatNumber} width={yAxisWidth} domain={leftAxisDomain} {...axisProps} />
                        {hasRightAxis && <YAxis yAxisId="right" orientation="right" tickFormatter={formatNumber} width={yAxisWidth} domain={rightAxisDomain} {...axisProps} />}
                        <Tooltip content={renderTooltip} />
                        <Legend layout="horizontal" verticalAlign="top" align="center" wrapperStyle={legendStyle} />
                        {renderAreas()}
                    </AreaChart>
                );
            case 'RadarChart':
                return (
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={convertedData}>
                        <PolarGrid stroke={CHART_THEME.polarGrid} />
                        <PolarAngleAxis dataKey={xAxisKey} tick={{ fill: CHART_THEME.axis, fontSize: 11 }} />
                        <PolarRadiusAxis tick={{ fill: CHART_THEME.axis, fontSize: 11 }} />
                        <Tooltip content={renderTooltip} />
                        <Legend wrapperStyle={legendStyle} />
                        {renderRadars()}
                    </RadarChart>
                );
            case 'ScatterChart':
                return (
                    <ScatterChart
                        margin={{
                            top: chartMargin.top + 6,
                            right: chartMargin.right,
                            bottom: chartMargin.bottom + 6,
                            left: chartMargin.left + 8,
                        }}
                    >
                        <CartesianGrid stroke={CHART_THEME.grid} strokeDasharray="3 4" />
                        <XAxis
                            type={typeof convertedData[0]?.[xAxisKey] === 'number' ? 'number' : 'category'}
                            dataKey={xAxisKey}
                            name={xAxisKey}
                            angle={xAxisAngle}
                            textAnchor={xAxisTextAnchor}
                            height={xAxisHeight}
                            {...axisProps}
                        />
                        <YAxis type="number" dataKey={yAxisArray[0]} name={yAxisArray[0]} unit="" {...axisProps} />
                        <Tooltip content={renderTooltip} cursor={{ strokeDasharray: '3 3' }} />
                        <Legend wrapperStyle={legendStyle} />
                        {renderScatter()}
                    </ScatterChart>
                );
            case 'RadialBarChart':
                return (
                    <RadialBarChart
                        cx="50%"
                        cy="50%"
                        innerRadius="20%"
                        outerRadius="80%"
                        barSize={10}
                        data={convertedData}
                    >
                        {renderRadialBars()}
                        <Legend iconSize={10} width={140} height={140} layout="vertical" verticalAlign="middle" align="right" wrapperStyle={legendStyle} />
                        <Tooltip content={renderTooltip} />
                    </RadialBarChart>
                );
            case 'ComposedChart':
                return renderComposedChart();
            default:
                return <p>{t("charts.unknownChartType", { type: chartType })}</p>;
        }
    };

    // Título: prop explícita tem prioridade; fallback para título embutido na config
    const resolvedTitle = title || config?.title || undefined;

    if (!hasRenderableData) {
        return (
            <div className={`p-5 border border-border rounded-xl bg-card shadow-sm ${className}`}>
                <p className="text-muted-foreground text-center">{t("charts.noData")}</p>
            </div>
        );
    }

    return (
        <div
            className={`p-5 border rounded-xl bg-card shadow-sm ${
                isDark ? 'border-border/80' : 'border-border/70'
            } ${className}`}
        >
            {resolvedTitle && (
                <h3 className="text-sm font-semibold mb-3 text-foreground tracking-tight">
                    {resolvedTitle}
                </h3>
            )}
            {isMobile && convertedData.length > 8 ? (
                <div className="overflow-x-auto -mx-1 px-1">
                    <div style={{ minWidth: `${Math.max(convertedData.length * 44, 320)}px`, width: '100%' }}>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            {renderChart()}
                        </ResponsiveContainer>
                    </div>
                </div>
            ) : (
                <div className="w-full">
                    <ResponsiveContainer width="100%" height={chartHeight}>
                        {renderChart()}
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

// Memoize RechartsRenderer to prevent expensive chart re-renders
export const RechartsRendererMemo = memo(RechartsRenderer);
